<script lang="ts" setup>
  import { useCart } from '@storefront/storefront';

  const emit = defineEmits({
    toggle: (section: string) => void 0,
  });

  defineProps({
    open: { type: Boolean, required: false, default: false },
  });

  const { cart } = useCart();

  function activate() {
    emit('toggle', 'discount');
  }
</script>
<template>
  <article class="order-discount-code">
    <template v-if="cart">
      <FormController v-slot="{ processing, submit }" :action="$route('web.cart.coupon.store')" aria-labelledby="order-discount-code__heading" class="order-discount-code__form" method="POST">
        <div :aria-pressed="open" aria-controls="order-discount-code__apply-discount" class="order-discount-code__header" role="button" tabindex="0" @click="activate" @keydown.space.prevent="activate">
          <h3 id="order-discount-code__heading" class="order-discount-code__heading">
            {{ $t('SHOP.CART.HEADER.HAVE_DISCOUNT_CODE') }}
          </h3>
          <SfIconPlus :class="{ 'order-discount-code__icon--open': open }" aria-hidden="true" class="order-discount-code__icon" />
        </div>
        <section id="order-discount-code__apply-discount" :class="{ 'order-discount-code__content--open': open }" :inert="!open" class="order-discount-code__content">
          <div class="order-discount-code__content-outer">
            <div class="order-discount-code__content-inner">
              <SfFormInput :label="$t('SHOP.CART.LABEL.DISCOUNT_CODE')" class="order-discount-code__input" name="code" required type="text" />
              <SfButton :loading="processing" class="order-discount-code__button-add" primary type="submit" @click="submit">
                {{ $t('SHOP.COMMON.ACTION.ADD') }}
              </SfButton>
            </div>
          </div>
        </section>
      </FormController>
    </template>
    <template v-if="cart && cart.coupons.length > 0">
      <CartCoupon v-for="coupon in cart.coupons" :coupon="coupon" />
    </template>
  </article>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-order-discount-transition-timing-func: ease-in-out;
    --sf-order-discount-transition-duration: 200ms;
  }

  .order-discount-code {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.875rem;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: text-decoration-color 0.2s ease-out;

      @media(hover: hover) {
        &:hover {
          text-decoration-color: var(--primary-color);
        }
      }
    }

    &__heading {
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-weight: var(--font-weight-regular);
    }

    &__icon {
      transform: rotate(0deg);
      transform-origin: 50% 50%;
      font-size: 1.25rem;
      line-height: 1;
      transition: transform;
      transition-duration: var(--sf-order-discount-transition-duration);
      transition-timing-function: var(--sf-order-discount-transition-timing-func);

      &--open {
        transform: rotate(45deg);
      }
    }

    &__content {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows;
      transition-duration: var(--sf-order-discount-transition-duration);
      transition-timing-function: var(--sf-order-discount-transition-timing-func);

      &--open {
        grid-template-rows: 1fr;
      }
    }

    &__content-outer {
      overflow: hidden;
    }

    &__content--open .order-discount-code__content-outer {
      overflow: visible;
    }

    &__content-inner {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      padding: 1rem 0.375rem;

      .control__error {
        padding: 0.25rem 0;
      }
    }
  }

  @include media.query(lg) {
    .order-discount-code {
      margin-top: 0.625rem;

      &__content {
        margin-top: 0.375rem;
      }
    }
  }
</style>
